import { Fragment, useMemo, useState, useEffect } from "react";
import { Container } from "@/components/container";
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from "@/partials/toolbar";
import { useLayout } from "@/providers";
import { useSnackbar } from "notistack";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useGetBookingReminderConfigQuery,
  useUpdateBookingReminderConfigMutation,
  useResetBookingReminderConfigMutation,
  BookingReminderConfig,
} from "@/services/BookingReminderConfigService";
import { ApiResponse } from "@/types/api.types";

const MIN_HOURS = 1;
const MAX_HOURS = 24;
const DEFAULT_HOURS = 24;

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "object" && error !== null) {
    const typedError = error as {
      data?: { message?: string };
      message?: string;
    };

    if (typedError.data?.message) {
      return typedError.data.message;
    }

    if (typedError.message) {
      return typedError.message;
    }
  }

  return fallback;
};

const BookingReminderConfiguration = () => {
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const canRead = hasPermission("booking_reminder_config");
  const canUpdate = hasPermission("booking_reminder_config");

  const { data, isLoading, isFetching, refetch } =
    useGetBookingReminderConfigQuery(undefined, {
      skip: !canRead,
    });

  const [updateConfig, { isLoading: isUpdating }] =
    useUpdateBookingReminderConfigMutation();
  const [resetConfig, { isLoading: isResetting }] =
    useResetBookingReminderConfigMutation();

  const initialHoursFromApi = useMemo<number>(() => {
    if (
      data &&
      typeof (data as ApiResponse<BookingReminderConfig>).data.hours ===
      "number"
    ) {
      return (data as ApiResponse<BookingReminderConfig>).data.hours;
    }
    return DEFAULT_HOURS;
  }, [data]);

  const [hours, setHours] = useState<number>(initialHoursFromApi);

  useEffect(() => {
    setHours(initialHoursFromApi);
  }, [initialHoursFromApi]);

  const handleHoursChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(event.target.value);
    if (Number.isNaN(value)) return;
    if (value < MIN_HOURS || value > MAX_HOURS) return;
    setHours(value);
  };

  const handleSave = async () => {
    try {
      await updateConfig({ hours }).unwrap();
      enqueueSnackbar("Booking reminder configuration updated successfully", {
        variant: "success",
      });
      refetch();
    } catch (error: unknown) {
      const message = getErrorMessage(error, "Failed to update configuration");
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleReset = async () => {
    try {
      await resetConfig().unwrap();
      enqueueSnackbar("Booking reminder configuration reset to default", {
        variant: "success",
      });
      setHours(DEFAULT_HOURS);
      refetch();
    } catch (error: unknown) {
      const message = getErrorMessage(error, "Failed to reset configuration");
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  if (!canRead) {
    return (
      <Fragment>
        {currentLayout?.name === "demo1-layout" && (
          <Container>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle />
                <ToolbarDescription>
                  Booking reminder configuration
                </ToolbarDescription>
              </ToolbarHeading>
            </Toolbar>
            <div className="bg-gray-100 p-6 rounded-lg shadow-lg flex flex-col">
              <div className="text-center py-4 text-gray-500">
                You don't have permission to view booking reminder
                configuration.
              </div>
            </div>
          </Container>
        )}
      </Fragment>
    );
  }

  const isBusy = isLoading || isFetching || isUpdating || isResetting;

  const hourOptions = Array.from({ length: MAX_HOURS - MIN_HOURS + 1 }).map(
    (_, index) => {
      const value = MIN_HOURS + index;
      return (
        <option key={value} value={value}>
          {value} hour{value > 1 ? "s" : ""} before booking
        </option>
      );
    },
  );

  return (
    <Fragment>
      {currentLayout?.name === "demo1-layout" && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="booking-reminder-configuration-heading" />
              <ToolbarDescription>
                Configure when booking reminders should be sent before the
                harbor check-in time.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={handleReset}
                disabled={isBusy || !canUpdate}
              >
                Reset to default ({DEFAULT_HOURS} hours)
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleSave}
                disabled={isBusy || !canUpdate}
              >
                {isBusy ? "Saving..." : "Save changes"}
              </button>
            </ToolbarActions>
          </Toolbar>
          <div className="flex flex-col gap-4">
            {isLoading && (
              <div
                className="text-center py-4"
                role="status"
                aria-live="polite"
              >
                Loading configuration...
              </div>
            )}
            {!isLoading && (
              <div className="flex flex-col gap-2 max-w-xs">
                <label
                  htmlFor="booking-reminder-hours"
                  className="block text-sm font-medium text-gray-700"
                >
                  Send booking reminder
                </label>
                <select
                  id="booking-reminder-hours"
                  className="select select-bordered h-10"
                  value={hours}
                  onChange={handleHoursChange}
                  disabled={isBusy || !canUpdate}
                  aria-label="Select how many hours before the booking the reminder should be sent"
                >
                  {hourOptions}
                </select>
                <p className="text-xs text-gray-500">
                  Choose between {MIN_HOURS} and {MAX_HOURS} hours before the
                  harbor check-in time. Default is {DEFAULT_HOURS} hours.
                </p>
              </div>
            )}
          </div>
        </Container>
      )}
    </Fragment>
  );
};

export { BookingReminderConfiguration };
